import posthog from 'posthog-js';
import React from 'react';

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: undefined };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: any) {
    console.error('Caught by ErrorBoundary:', error, info);
    // React does not rethrow errors a boundary catches, so exception autocapture
    // never sees them. No-op until PostHog is initialised (production only).
    posthog.captureException(error, {
      component_stack: info?.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.body}>
          <div style={styles.container}>
            <svg
              style={styles.icon}
              viewBox="0 0 120 120"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="60"
                cy="60"
                r="50"
                fill="#fef2f2"
                stroke="#ef4444"
                strokeWidth="2"
              />
              <path
                d="M60 30 L60 65"
                stroke="#ef4444"
                strokeWidth="6"
                strokeLinecap="round"
              />
              <circle cx="60" cy="80" r="4" fill="#ef4444" />
            </svg>

            <div style={styles.errorCode}>500</div>

            <h1 style={styles.title}>Oops! Something went wrong</h1>

            <p style={styles.description}>
              An unexpected error occurred. The issue has been recorded. Please
              go back or go dashboard to continue. Afterwards, you can contact
              support through chat if needed
            </p>

            <div style={styles.buttonGroup}>
              <button
                style={{ ...styles.button, ...styles.primaryBtn }}
                onClick={() => location.reload()}
              >
                Refresh Page
              </button>
              <button
                style={{ ...styles.button, ...styles.secondaryBtn }}
                onClick={() => {
                  location.replace('/app/dashboard/awaiting-actions');
                }}
              >
                Go To Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Inline styles (converted from your HTML CSS)
const styles: { [key: string]: React.CSSProperties } = {
  body: {
    background: 'var(--sk-fill-tertiary)',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  container: {
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
    padding: '60px 40px',
    maxWidth: 500,
    width: '100%',
    textAlign: 'center',
    animation: 'slideUp 0.5s ease-out',
  },
  icon: {
    width: 120,
    height: 120,
    margin: '0 auto 30px',
    animation: 'float 3s ease-in-out infinite',
  },
  errorCode: {
    fontSize: 72,
    fontWeight: 800,
    marginBottom: 40,
    color: 'var(--sk-danger-fg)',
  },
  title: {
    fontSize: 28,
    color: '#2d3748',
    marginBottom: 15,
    fontWeight: 700,
  },
  description: {
    color: '#718096',
    fontSize: 16,
    lineHeight: 1.6,
    marginBottom: 35,
  },
  buttonGroup: {
    display: 'flex',
    gap: 15,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  button: {
    padding: '13px 25px',
    border: 'none',
    borderRadius: 5,
    fontSize: 16,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
  },
  primaryBtn: {
    background: 'var(--sk-primary)',
    color: 'white',
  },
  secondaryBtn: {
    border: '1px solid var(--sk-border-subtle)',
    color: 'var(--sk-fg)',
    background: 'white',
  },
};

export default ErrorBoundary;
